/**
 * AdminUserDetail — /admin/users/:userId
 *
 * A single hub page for everything an admin might want to do to one
 * member: view profile + status, see moderation log timeline, change
 * role, promote to expert, and change status (active / inactive /
 * read-only 24h / suspend N days / ban permanent) — plus a suggested
 * "next tier" callout based on the 30-day rolling escalation ladder.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, ShieldAlert, ShieldCheck, Ban, UserCheck, UserX, Clock,
  Loader2, Mail, IdCard, Sparkles, ArrowUp, ArrowDown, MessageCircle, KeyRound,
  MailCheck, MailX,
} from 'lucide-react';
import ModerationActionModal from '../../components/admin/ModerationActionModal';
import AdminUserFormModal from '../../components/admin/AdminUserFormModal';

const STATUS_LABEL = {
  active:    { label: 'Active',    dot: 'bg-emerald-500' },
  inactive:  { label: 'Inactive',  dot: 'bg-slate-400' },
  suspended: { label: 'Suspended', dot: 'bg-amber-500' },
  banned:    { label: 'Banned',    dot: 'bg-rose-600' },
  read_only: { label: 'Read-only', dot: 'bg-orange-500' },
};

const ACTION_META = {
  moderation_action_composite: { icon: ShieldAlert,  color: 'text-rose-600', label: 'Composite moderation action' },
  status_change_active:        { icon: UserCheck,    color: 'text-emerald-600', label: 'Restored to active' },
  status_change_inactive:      { icon: UserX,        color: 'text-slate-500',   label: 'Marked inactive' },
  status_change_suspended:     { icon: Clock,        color: 'text-amber-600',   label: 'Suspended' },
  status_change_banned:        { icon: Ban,          color: 'text-rose-700',    label: 'Banned' },
  status_change_read_only:     { icon: ShieldAlert,  color: 'text-orange-600',  label: 'Read-only hold' },
  lift_soft_ban:               { icon: UserCheck,    color: 'text-emerald-600', label: 'Soft-ban lifted' },
  role_change:                 { icon: Sparkles,     color: 'text-primary',     label: 'Role changed' },
  expert_flag:                 { icon: Sparkles,     color: 'text-primary',     label: 'Expert flag changed' },
};

const relTime = (iso) => {
  try {
    const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (secs < 60)      return `${secs}s ago`;
    if (secs < 3600)    return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400)   return `${Math.floor(secs / 3600)}h ago`;
    return `${Math.floor(secs / 86400)}d ago`;
  } catch { return ''; }
};

const AdminUserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { API } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(false);
  const [status, setStatus] = useState('active');
  const [statusReason, setStatusReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [savingStatus, setSavingStatus] = useState(false);
  const [showModModal, setShowModModal] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${userId}`);
      setData(r.data);
      setStatus(r.data.user?.status || 'active');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load user');
    } finally { setLoading(false); }
  }, [API, userId]);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (newRole) => {
    setSavingRole(true);
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, {
        role: newRole,
        reason: `Role changed to ${newRole} from admin detail page`,
      });
      toast.success(`Role set to ${newRole}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change role'); }
    finally { setSavingRole(false); }
  };

  const toggleExpert = async () => {
    const next = !data?.user?.is_expert;
    try {
      await axios.put(`${API}/admin/users/${userId}/expert-flag`, {
        is_expert: next,
        reason: next ? 'Flagged as expert via detail page' : 'Un-flagged via detail page',
      });
      toast.success(next ? 'Marked as expert' : 'Expert flag removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update expert flag'); }
  };

  const saveStatus = async () => {
    setSavingStatus(true);
    try {
      const payload = { status, reason: statusReason.trim() || null };
      if (status === 'suspended') payload.suspend_days = Number(suspendDays) || 7;
      await axios.put(`${API}/admin/users/${userId}/status`, payload);
      toast.success(`Status updated to ${status}`);
      setStatusReason('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change status'); }
    finally { setSavingStatus(false); }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading user…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-muted-foreground">
        Could not load this user. <Link to="/admin/users" className="underline">Back to user list</Link>
      </div>
    );
  }

  const u = data.user;
  const statusMeta = STATUS_LABEL[u.status || 'active'] || STATUS_LABEL.active;
  const sug = data.suggested_action;

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-user-detail">
      <div className="mb-4">
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to user list
        </Link>
      </div>

      {/* Header */}
      <div className="pro-card p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="h-14 w-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0 text-xl font-heading font-extrabold">
          {(u.display_name || u.email || '?').slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-2xl font-extrabold truncate">
              {u.display_name || u.email}
            </h1>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-muted/70 px-2 py-0.5" data-testid="user-status-pill">
              <span className={`h-2 w-2 rounded-full ${statusMeta.dot}`} /> {statusMeta.label}
            </span>
            {u.role === 'admin' && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-primary/15 text-primary border border-primary/40 px-2 py-0.5">
                <ShieldCheck className="w-3 h-3" /> Admin
              </span>
            )}
            {u.is_expert && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-primary/10 text-primary border border-primary/40 px-2 py-0.5">
                <Sparkles className="w-3 h-3" /> Expert
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>
            <span className="inline-flex items-center gap-1"><IdCard className="w-3 h-3" /> {u.id?.slice(0, 8)}…</span>
            {u.created_at && <span>Joined {new Date(u.created_at).toLocaleDateString()}</span>}
          </div>
          {u.read_only_until && u.status === 'read_only' && (
            <div className="mt-2 text-[11px] font-bold text-orange-700">
              Read-only until {new Date(u.read_only_until).toLocaleString()} — {u.read_only_reason}
            </div>
          )}
          {u.suspended_until && u.status === 'suspended' && (
            <div className="mt-2 text-[11px] font-bold text-amber-700">
              Suspended until {new Date(u.suspended_until).toLocaleString()}
              {u.status_reason ? ` — ${u.status_reason}` : ''}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            className="rounded-full font-bold"
            onClick={() => navigate('/messages')}
            data-testid="user-detail-dm-btn"
          >
            <MessageCircle className="w-4 h-4 mr-1.5" /> Message
          </Button>
          <Button
            variant="outline"
            className="rounded-full font-bold"
            onClick={() => setShowResetPw(true)}
            data-testid="user-detail-reset-pw-btn"
          >
            <KeyRound className="w-4 h-4 mr-1.5" /> Reset password
          </Button>
          <Button
            className="rounded-full font-bold bg-rose-600 text-white hover:bg-rose-700"
            onClick={() => setShowModModal(true)}
            disabled={u.role === 'admin'}
            data-testid="user-detail-mod-action-btn"
          >
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Moderation actions…
          </Button>
        </div>
      </div>

      {/* Suggested escalation callout */}
      {sug && u.role !== 'admin' && (
        <div className="mt-4 rounded-2xl border-2 border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="suggested-escalation">
          <ShieldAlert className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Suggested next tier</div>
            <div className="mt-0.5 font-heading text-lg font-extrabold text-foreground">{sug.label}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {sug.reason} · <span className="font-semibold">{data.offenses_30d}</span> prior enforcement action(s) in the last 30 days.
            </div>
          </div>
        </div>
      )}

      {/* Email delivery status — surface welcome + last password-reset
          delivery so operators can see who has and hasn't received their
          onboarding / recovery emails. Fields are persisted by the admin
          endpoints on the users collection. */}
      <div className="mt-4 pro-card p-4" data-testid="user-email-delivery">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Email delivery</div>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Welcome email */}
          <div className="rounded-xl border border-border p-3" data-testid="user-welcome-email-row">
            <div className="flex items-center gap-2">
              {u.welcome_email_sent_at ? (
                <MailCheck className="w-4 h-4 text-emerald-600" />
              ) : (
                <MailX className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Welcome email
              </div>
            </div>
            <div className="mt-1 text-sm font-semibold text-foreground">
              {u.welcome_email_sent_at ? (
                <>
                  Sent {relTime(u.welcome_email_sent_at)}
                  {u.welcome_email_backfilled && (
                    <span className="ml-1 text-[10px] uppercase tracking-widest font-bold text-amber-700">
                      (backfill)
                    </span>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground italic font-normal">Not sent</span>
              )}
            </div>
            {u.welcome_email_sent_at && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {new Date(u.welcome_email_sent_at).toLocaleString()}
                {u.welcome_email_id && (
                  <span className="ml-1 font-mono text-[10px] opacity-70">
                    · id {String(u.welcome_email_id).slice(0, 8)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Password reset email */}
          <div className="rounded-xl border border-border p-3" data-testid="user-reset-email-row">
            <div className="flex items-center gap-2">
              {u.password_reset_email_sent_at ? (
                <MailCheck className="w-4 h-4 text-emerald-600" />
              ) : (
                <MailX className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Last password reset email
              </div>
            </div>
            <div className="mt-1 text-sm font-semibold text-foreground">
              {u.password_reset_email_sent_at ? (
                <>Sent {relTime(u.password_reset_email_sent_at)}</>
              ) : (
                <span className="text-muted-foreground italic font-normal">Never</span>
              )}
            </div>
            {u.password_reset_email_sent_at && (
              <div className="mt-0.5 text-[11px] text-muted-foreground">
                {new Date(u.password_reset_email_sent_at).toLocaleString()}
                {u.password_reset_email_id && (
                  <span className="ml-1 font-mono text-[10px] opacity-70">
                    · id {String(u.password_reset_email_id).slice(0, 8)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activity counts */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="user-activity-counts">
        {[
          { label: 'Topics',          value: data.counts.topics },
          { label: 'Replies',         value: data.counts.replies },
          { label: 'DMs sent',        value: data.counts.dms },
          { label: 'Reports against', value: data.counts.reports_against },
        ].map((k) => (
          <div key={k.label} className="rounded-xl border border-border p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{k.label}</div>
            <div className="mt-1 font-heading text-2xl font-extrabold text-foreground">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Role management */}
      <div className="mt-4 pro-card p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Role</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground">Current: {u.role || 'member'}</span>
          {u.role === 'admin' ? (
            <Button
              size="sm" variant="outline"
              disabled={savingRole}
              onClick={() => changeRole('member')}
              className="rounded-full font-semibold"
              data-testid="role-demote-btn"
            >
              <ArrowDown className="w-3.5 h-3.5 mr-1" /> Demote to member
            </Button>
          ) : (
            <Button
              size="sm" variant="outline"
              disabled={savingRole}
              onClick={() => changeRole('admin')}
              className="rounded-full font-semibold text-primary border-primary/40"
              data-testid="role-promote-btn"
            >
              <ArrowUp className="w-3.5 h-3.5 mr-1" /> Promote to admin
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={toggleExpert}
            className={`rounded-full font-semibold ${u.is_expert ? 'text-primary border-primary/40' : 'text-muted-foreground'}`}
            data-testid="role-toggle-expert-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {u.is_expert ? 'Un-expert' : 'Mark expert'}
          </Button>
        </div>
      </div>

      {/* Status management */}
      <div className="mt-4 pro-card p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Status</div>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-input bg-background px-3 h-10 text-sm md:col-span-1"
            data-testid="user-status-select"
            disabled={u.role === 'admin'}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive (soft archive)</option>
            <option value="read_only">Read-only (24h)</option>
            <option value="suspended">Suspended…</option>
            <option value="banned">Banned (permanent)</option>
          </select>
          {status === 'suspended' && (
            <Input
              type="number"
              min={1}
              max={365}
              value={suspendDays}
              onChange={(e) => setSuspendDays(e.target.value)}
              className="md:col-span-1"
              data-testid="user-suspend-days"
              placeholder="Days"
            />
          )}
          <Textarea
            rows={1}
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Reason (visible in moderation log)"
            className={status === 'suspended' ? 'md:col-span-1' : 'md:col-span-2'}
            data-testid="user-status-reason"
          />
          <Button
            onClick={saveStatus}
            disabled={savingStatus || u.role === 'admin'}
            className="rounded-full font-bold md:col-span-1"
            data-testid="user-status-save-btn"
          >
            {savingStatus ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Apply status
          </Button>
        </div>
      </div>

      {/* Moderation log */}
      <div className="mt-4 pro-card p-4">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Moderation timeline</div>
        {(!data.moderation_log || data.moderation_log.length === 0) ? (
          <p className="text-sm text-muted-foreground italic mt-2">No moderation actions on this member yet.</p>
        ) : (
          <ol className="mt-3 space-y-2" data-testid="moderation-log-list">
            {data.moderation_log.map((m) => {
              const meta = ACTION_META[m.action] || { icon: ShieldAlert, color: 'text-muted-foreground', label: m.action };
              const Icon = meta.icon;
              return (
                <li key={m.id} className="rounded-lg border border-border px-3 py-2 flex items-start gap-2">
                  <Icon className={`w-4 h-4 mt-0.5 ${meta.color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">
                      {meta.label}
                      {m.new_status && <> → <code className="text-xs">{m.new_status}</code></>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {m.admin_email || 'system'} · {relTime(m.timestamp)}
                      {m.reason ? ` · ${m.reason}` : ''}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {showModModal && (
        <ModerationActionModal
          open={showModModal}
          onClose={() => setShowModModal(false)}
          targetUser={{
            id:           u.id,
            email:        u.email,
            display_name: u.display_name,
            role:         u.role,
          }}
          onDone={() => { setShowModModal(false); load(); }}
        />
      )}
      {showResetPw && (
        <AdminUserFormModal
          open={showResetPw}
          mode="reset"
          onClose={() => setShowResetPw(false)}
          targetUser={{ id: u.id, email: u.email }}
          onDone={() => { setShowResetPw(false); load(); }}
        />
      )}
    </div>
  );
};

export default AdminUserDetail;
